import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, verify2FALogin, clearError } from '../redux/authSlice';
import { MessageCircle, Mail, Lock, Loader, AlertCircle, ShieldCheck, ArrowLeft, QrCode, Eye, EyeOff } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import Seo from '../components/Seo';
import { cn } from '../utils/cn';

function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, twoFactorRequired, tempToken } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [mfaCode, setMfaCode] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Clear any lingering auth errors on mount
    dispatch(clearError());
  }, [dispatch]);

  const validate = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = 'Work email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) setValidationErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const result = await dispatch(loginUser(formData));
    if (result.meta.requestStatus === 'fulfilled' && !result.payload?.twoFactorRequired) {
      handlePostLogin(result.payload?.company?.role);
    }
  };

  const handleMfaSubmit = async (e) => {
    if (e) e.preventDefault();
    if (mfaCode.length !== 6) return;
    
    const result = await dispatch(verify2FALogin({ tempToken, code: mfaCode }));
    if (result.meta.requestStatus === 'fulfilled') {
      handlePostLogin(result.payload?.company?.role);
    }
  };

  const handlePostLogin = (role) => {
    if (role === 'superadmin') navigate('/superadmin/dashboard');
    else if (role === 'admin') navigate('/admin/dashboard');
    else navigate('/dashboard');
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#F3F3F3] px-4 py-8">
      <Seo
        title={`${twoFactorRequired ? 'Two-Factor Verification' : 'Login'} | WaapLeads CRM`}
        description="Securely access your WaapLeads CRM workspace to manage customer engagement, analytics, and messaging infrastructure."
        pathname="/login"
      />
      
      {/* ── RIGHT SIDE: DYNAMIC AUTH FORM ── */}
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-lg p-6 sm:p-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="w-full">
          {!twoFactorRequired ? (
            /* STANDARD LOGIN VIEW */
            <div className="animate-nebula-fade-in">
              <div className="flex flex-col items-center mb-6 text-center">
                <BrandLogo size={40} showText={false} className="mb-4" />
                <h1 className="text-xl font-bold text-[#2E2E2E] mb-1">Welcome Back</h1>
                <p className="text-[13px] text-[#5C5C5C] font-medium">Sign in to your account</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-[#FEF1EE] border border-[#FDDDE3] rounded flex items-center gap-3 text-[#BA0517] text-[13px] font-semibold">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2 slds-input-group">
                  <label htmlFor="email" className="text-[#5C5C5C] text-[12px] font-semibold">Work Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={16} />
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={cn("w-full pl-10 h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]", validationErrors.email && "border-[#BA0517]")}
                      placeholder="name@company.com"
                      autoComplete="email"
                    />
                  </div>
                  {validationErrors.email && <p className="text-[#BA0517] text-[11px] font-medium mt-1 ml-1">{validationErrors.email}</p>}
                </div>

                <div className="space-y-2 slds-input-group">
                  <div className="flex justify-between items-center mb-1 px-1">
                    <label htmlFor="password" style={{ margin: 0 }} className="text-[#5C5C5C] text-[12px] font-semibold">Password</label>
                    <Link to="/forgot-password" style={{ color: '#0176D3', fontSize: '12px', fontWeight: 700 }} className="hover:underline transition-all">Forgot Password?</Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" size={16} />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={cn("w-full pl-10 pr-12 h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]", validationErrors.password && "border-[#BA0517]")}
                      placeholder="••••••••"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-3 flex items-center text-[#706E6B] hover:text-[#2E2E2E] transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {validationErrors.password && <p className="text-[#BA0517] text-[11px] font-medium mt-1 ml-1">{validationErrors.password}</p>}
                </div>

                <button 
                  type="submit" 
                  className="w-full h-9 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <Loader size={18} className="animate-spin" /> 
                      <span>Authenticating…</span>
                    </span>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>

              <div className="mt-6 text-center pt-4 border-t border-[#E5E5E5]">
                <p className="text-[13px] text-[#5C5C5C] font-medium">
                  New to WaapLeads? <Link to="/register" className="text-[#0176D3] font-bold hover:underline ml-1">Create an Account</Link>
                </p>
              </div>
            </div>
          ) : (
            /* 2FA VERIFICATION VIEW */
            <div className="animate-nebula-fade-in">
              <div className="flex flex-col items-center mb-6 text-center">
                <div className="w-20 h-20 bg-[#EEF4FF] rounded-lg flex items-center justify-center text-[#0176D3] mb-6 border border-[#D8E6FE]">
                  <ShieldCheck size={40} />
                </div>
                <h1 className="text-xl font-bold text-[#2E2E2E] mb-1">Verification</h1>
                <p className="text-[13px] text-[#5C5C5C] font-medium">Enter your 6-digit MFA code</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-[#FEF1EE] border border-[#FDDDE3] rounded flex items-center gap-3 text-[#BA0517] text-[13px] font-semibold">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleMfaSubmit} className="space-y-8">
                <div className="space-y-4">
                  <div className="relative">
                    <QrCode className="absolute left-4 top-1/2 -translate-y-1/2 text-[#706E6B]" size={20} />
                    <input
                      id="mfaCode"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength="6"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full pl-12 h-12 text-center text-2xl font-mono border border-[#D8DDE6] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] rounded transition-all"
                      placeholder="000000"
                      autoFocus
                    />
                  </div>
                  <p className="text-center text-[12px] text-[#5C5C5C] font-medium">
                    Please open your authenticator app and enter the code for your WaapLeads account.
                  </p>
                </div>

                <div className="flex flex-col gap-4">
                  <button 
                    type="submit" 
                    className="w-full h-9 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold disabled:opacity-50"
                    disabled={mfaCode.length !== 6 || loading}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-3">
                        <Loader size={20} className="animate-spin" /> 
                        <span>Verifying…</span>
                      </span>
                    ) : (
                      "Confirm Code"
                    )}
                  </button>
                  
                  <button 
                    type="button"
                    onClick={() => window.location.reload()}
                    className="flex items-center justify-center gap-2 text-[13px] font-bold text-[#706E6B] hover:text-[#2E2E2E] transition-colors py-2"
                  >
                    <ArrowLeft size={16} />
                    Back to Login
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
